import { useEffect, useState, useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useGridVariant } from '~/builder/utils/gridVariantContext';
import { CIO_BROWSE_RESULTS } from '~/shared/constants/queryCacheKeys';
import { getExpandedPlusSizeFilters } from '~/shared/utils/plusSizeFilters';
import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import { useCioClient } from '~/shared/utils/useCioClient';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useDomain } from '~/techstyle-shared/redux-core';

import {
  ConstructorGridFilterNames,
  ConstructorGridFilterTypes,
} from '../../builder/components/BuilderConstructorGrid/BuilderConstructorGrid.builder';
import getCioBrowseResults from '../api/getCioBrowseResults';
import {
  BROWSE_API_RESULTS_STALE_TIME,
  CUSTOM_COLOR_FILTER,
} from '../constants';
import { normalizeCioResponse } from '../transformers/normalizeCioResponse';
import { combineCustomFacetFilters } from '../utils/filters';
import { getSelectedSortOption } from '../utils/sortOptions';

import useGridInputs from './useGridInputs';

const getFilterNameAndFilterValue = ({
  filterType,
  collectionIdOverride,
  collectionSelection,
  categoryIdOverride,
  categorySelection,
  facet,
  facetOption,
}) => {
  switch (filterType) {
    case ConstructorGridFilterTypes.Collection:
      return {
        cioFilterName: ConstructorGridFilterNames.Collection,
        cioFilterValue: collectionIdOverride || collectionSelection?.value,
      };
    case ConstructorGridFilterTypes.Category:
      return {
        cioFilterName: ConstructorGridFilterNames.Category,
        cioFilterValue: categoryIdOverride || categorySelection?.value,
      };
    case ConstructorGridFilterTypes.Facet:
      return { cioFilterName: facet, cioFilterValue: facetOption };
    default:
      return { cioFilterName: '', cioFilterValue: '' };
  }
};

const useCioBrowseProducts = ({ selectedUrlFilters, allRegisteredFacets }) => {
  const cioClient = useCioClient();
  const { isVip, isElite } = useMembership();
  const builderConstructorGridInputs = useGridInputs();
  const { tld } = useDomain();
  const isSecondarySite = useSecondarySite();
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { isSaleGrid } = useGridVariant();
  const { excludeFplCodes, excludeTagIds } = useAssortmentTestConfigs();

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const urlParams = new URLSearchParams(query);

  const { cioFilterName, cioFilterValue } = getFilterNameAndFilterValue(
    builderConstructorGridInputs
  );

  const selectedSortOption = getSelectedSortOption(
    urlParams,
    builderConstructorGridInputs?.sortOptions
  );

  // We want to combine XXL, XXL-1X, 1X into just XXL-1X. We have replaced the
  // XXL and 1X options to just XXL-1X, now we need to update the filters we query
  // to include the XXL and 1X options when XXL-1X is selected.
  const parsedUrlFilters = getExpandedPlusSizeFilters(selectedUrlFilters);

  const [customFacetFilters, setCustomFacetFilters] = useState({});
  const hiddenFacetFilters = combineCustomFacetFilters(customFacetFilters);

  const dynamicQueryParams = {
    cioFilterName,
    cioFilterValue,
    isElite,
    isVip,
    selectedUrlFilters: parsedUrlFilters,
    selectedSortOption,
    tld,
    removeOriginFlag,
    isSecondarySite,
    isSaleGrid,
    excludeTagIds,
    excludeFplCodes,
    hiddenFacetFilters,
  };

  const infiniteQueryResults = useInfiniteQuery({
    queryKey: [CIO_BROWSE_RESULTS, dynamicQueryParams],
    queryFn: ({ pageParam = 1 }) => {
      return getCioBrowseResults({
        cioClient,
        page: pageParam,
        ...dynamicQueryParams,
      });
    },
    /**
     * Memoize the select transform to ensure React Query only re-runs the transformation
     * when the actual data changes, not on every component re-render.
     *
     * Without useMemo, a new function would be created on each render, causing React Query
     * to think the select function changed and re-execute the transformation unnecessarily.
     *
     * The empty dependency array [] ensures the function reference remains stable across
     * renders, while React Query's internal caching handles calling it only when new data
     * is fetched from the API.
     */
    select: useMemo(
      () => (response) => {
        return normalizeCioResponse(response);
      },
      []
    ),
    enabled: Boolean(cioClient) && Boolean(allRegisteredFacets),
    staleTime: BROWSE_API_RESULTS_STALE_TIME,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextPage;
    },
    // We need keep previous data to be true so that we can show the disabled options state
    keepPreviousData: true,
  });

  useEffect(() => {
    const colorFilter = customFacetFilters[CUSTOM_COLOR_FILTER.name];
    const tagIds = colorFilter?.filters?.tag_id_list;

    if (!tagIds?.length) {
      return;
    }

    const results =
      infiniteQueryResults?.data?.pages?.[0]?.response?.results ?? [];
    const hasResults = results.length > 0;

    if (hasResults) {
      return;
    }

    setCustomFacetFilters((prev) => {
      const prevColorFilter = prev[CUSTOM_COLOR_FILTER.name];
      if (!prevColorFilter) {
        return prev;
      }

      return {
        ...prev,
        [CUSTOM_COLOR_FILTER.name]: {
          ...prevColorFilter,
          hasError: true,
        },
      };
    });
  }, [customFacetFilters, infiniteQueryResults, setCustomFacetFilters]);

  return {
    cioFilterName,
    cioFilterValue,
    selectedSortOption,
    customFacetFilters,
    setCustomFacetFilters,
    ...infiniteQueryResults,
  };
};

export default useCioBrowseProducts;
